import { supabase } from '../lib/supabase';

// =====================================================
// FEATURE GATE SERVICE
// Controla acesso a features baseado em planos
// =====================================================

export type FeatureType = 
  | 'sos_consultation'          // Consultas SOS
  | 'prescription_analysis'     // Análise de Prescrição IA
  | 'family_member';            // Adicionar membro na família

export interface FeatureAccessResult {
  allowed: boolean;
  reason: 'ok' | 'unlimited' | 'limit_reached' | 'feature_type_invalid';
  message?: string;
  limit?: number;
  used?: number;
  remaining?: number;
  quota_type?: 'lifetime' | 'monthly' | 'trial_days';
  resets_at?: string | null;
}

export interface ConsumeFeatureResult {
  success: boolean;
  used_count?: number;
  limit?: number;
  remaining?: number;
  quota_type?: 'lifetime' | 'monthly';
  error?: string;
  reason?: string;
}

class FeatureGateService {
  // Cache simples para reduzir chamadas repetidas
  private cache = new Map<string, { time: number; result: FeatureAccessResult }>();
  private readonly CACHE_TTL_MS = 3000; // 3s
  /**
   * Verifica se o usuário pode usar uma feature
   * @param userId - ID do usuário
   * @param featureType - Tipo de feature
   * @returns Resultado com allowed, limit, used, remaining
   */
  async canUseFeature(
    userId: string,
    featureType: FeatureType
  ): Promise<FeatureAccessResult> {
    // Cache TTL simples para evitar chamadas repetidas em curto intervalo
    const cacheKey = `${userId}:${featureType}`;
    const nowTs = Date.now();
    const cached = this.cache.get(cacheKey);
    if (cached && (nowTs - cached.time) < this.CACHE_TTL_MS) {
      return cached.result;
    }
    try {
      const { data, error } = await supabase.rpc('can_use_feature', {
        p_feature_key: featureType,
        p_user_id: userId,
      });

      if (error) {
        console.error('[FeatureGate] Erro ao verificar feature:', error);
        throw error;
      }

      // Padronizar resposta do RPC para FeatureAccessResult (RETURNS TABLE → array)
      const raw = (Array.isArray(data) ? (data?.[0] || {}) : (data || {})) as any;
      const plan = (raw.current_plan as string | undefined)?.toLowerCase() as 'free' | 'basic' | 'premium' | undefined;
      const limitValueRaw = raw.limit_value as string | number | undefined;
      const isUnlimited = String(limitValueRaw).toLowerCase() === 'unlimited';
      const limit = isUnlimited ? Number.POSITIVE_INFINITY : Number.parseInt(String(limitValueRaw ?? '0'), 10);
      const used = Number(raw.usage_count ?? 0);
      const allowed = Boolean(raw.allowed);

      let reason: 'ok' | 'unlimited' | 'limit_reached' = 'ok';
      if (isUnlimited) reason = 'unlimited';
      else reason = allowed ? 'ok' : 'limit_reached';

      const quota_type: 'lifetime' | 'monthly' | 'trial_days' | undefined =
        plan === 'free' ? 'lifetime' : 'monthly';

      // Backend agora agrega buckets de plano + avulso (sos_consultation + sos_additional)
      const credits = Number(raw.credits ?? 0);
      const normalized: FeatureAccessResult = {
        allowed,
        reason,
        limit: Number.isFinite(limit) ? limit : undefined,
        used,
        remaining: credits > 0 ? credits : (Number.isFinite(limit) ? Math.max(0, (limit as number) - used) : undefined),
        quota_type,
        resets_at: null,
        message: raw.reason || undefined, // Breakdown (ex: "2 crédito(s) — plano: 0, avulso: 2")
      };

      if ((globalThis as any)?.__DEBUG_FEATURE_GATE) {
        console.debug(`[FeatureGate] Verificação ${featureType} (normalized):`, normalized);
      }
      this.cache.set(cacheKey, { time: Date.now(), result: normalized });
      return normalized;
    } catch (error) {
      console.error('[FeatureGate] Erro ao verificar feature:', error);
      // Em caso de erro, bloqueia por segurança
      return {
        allowed: false,
        reason: 'feature_type_invalid',
        message: 'Erro ao verificar permissão. Tente novamente.',
      };
    }
  }

  /**
   * Registra uso de uma feature (incrementa contador)
   * @param userId - ID do usuário
   * @param featureType - Tipo de feature
   * @param featureId - ID opcional do registro (ex: sos_consultations.id)
   * @returns Resultado com success, used_count, remaining
   */
  async consumeFeature(
    userId: string,
    featureType: FeatureType,
    featureId?: string
  ): Promise<ConsumeFeatureResult> {
    try {
      const { data, error } = await supabase.rpc('consume_feature', {
        p_feature_key: featureType,
        p_user_id: userId,
        p_feature_id: featureId || null,
        p_metadata: {},
      });

      if (error) {
        console.error('[FeatureGate] Erro ao consumir feature:', error);
        throw error;
      }
      // Normalizar resposta do RPC (pode ser boolean, objeto ou array)
      let normalized: ConsumeFeatureResult;
      if (typeof data === 'boolean') {
        normalized = { success: data };
      } else if (Array.isArray(data)) {
        const row: any = data?.[0] || {};
        normalized = {
          success: row.success !== undefined ? Boolean(row.success) : true,
          used_count: row.used_count ?? row.usage_count ?? undefined,
          limit: row.limit ?? row.limit_value ?? undefined,
          remaining: row.remaining ?? undefined,
          quota_type: row.quota_type ?? undefined,
          reason: row.reason ?? undefined,
        };
      } else if (data && typeof data === 'object') {
        const obj: any = data;
        normalized = {
          success: obj.success !== undefined ? Boolean(obj.success) : true,
          used_count: obj.used_count ?? obj.usage_count ?? undefined,
          limit: obj.limit ?? obj.limit_value ?? undefined,
          remaining: obj.remaining ?? undefined,
          quota_type: obj.quota_type ?? undefined,
          reason: obj.reason ?? undefined,
        };
      } else {
        normalized = { success: false, error: 'invalid_response' };
      }

      if ((globalThis as any)?.__DEBUG_FEATURE_GATE) {
        console.debug(`[FeatureGate] Feature consumida ${featureType}:`, normalized);
      }
      // Invalida cache para refletir imediatamente o novo uso
      try {
        const cacheKey = `${userId}:${featureType}`;
        this.cache.delete(cacheKey);
      } catch {
        // Silenciosamente ignorar erro ao invalidar cache
      }
      return normalized;
    } catch (error) {
      console.error('[FeatureGate] Erro ao consumir feature:', error);
      return {
        success: false,
        error: 'Erro ao registrar uso. Tente novamente.',
      };
    }
  }

  /**
   * Verifica e consome feature em uma única chamada (helper)
   * Útil para fluxos simples: verifica → usa → registra
   * @param userId - ID do usuário
   * @param featureType - Tipo de feature
   * @returns Resultado da verificação
   */
  async checkAndConsumeFeature(
    userId: string,
    featureType: FeatureType
  ): Promise<FeatureAccessResult> {
    // 1. Verifica se pode usar
    const access = await this.canUseFeature(userId, featureType);

    // 2. Se não pode, retorna imediatamente
    if (!access.allowed) {
      return access;
    }

    // 3. Se pode, consome (registra uso)
    await this.consumeFeature(userId, featureType);

    // 4. Retorna resultado
    return access;
  }

  /**
   * Formata mensagem amigável baseada no resultado
   * @param result - Resultado da verificação
   * @param featureName - Nome amigável da feature (ex: "Consultas SOS")
   * @returns Mensagem formatada
   */
  formatMessage(result: FeatureAccessResult, featureName: string): string {
    if (result.allowed) {
      if (result.quota_type === 'trial_days') {
        return `Você ainda tem ${result.remaining} dias de ${featureName} ilimitado.`;
      }
      return `Você ainda tem ${result.remaining} ${featureName} disponíveis.`;
    }

    if (result.quota_type === 'trial_days') {
      return `Seu período de ${result.limit} dias de ${featureName} ilimitado expirou. Faça upgrade para continuar!`;
    }

    if (result.quota_type === 'lifetime') {
      return `Você atingiu o limite vitalício de ${result.limit} ${featureName}. Faça upgrade para continuar!`;
    }

    return `Você atingiu o limite mensal de ${result.limit} ${featureName}. Renova no dia 1º ou faça upgrade!`;
  }
}

export const featureGateService = new FeatureGateService();
export default featureGateService;
